import { useEffect, useMemo, useReducer, useRef } from "react";
import S from "@/lib/sound";
import { serverNow } from "@/lib/api";
import { WEATHER_TIMING_MS } from "@/lib/weatherTiming";
import { LAYER } from "@/lib/terrain";

// WEATHER v2 board overlay.
// Shape faces — AREA SHUFFLE: decoy areas of the real area's size (ws.shuffle.decoys, made by
// the server with the result, so every screen shows the same decoys at the same server time)
// jump around the board one at a time, fast at first and slowing down (ws.shuffle.times_ms);
// the last few land close to the real area. At freeze_ms the real area appears and FREEZES with
// a short pulse, then the reach squares (one step outside it) get a soft dashed outline.
// Sounds: a soft tick per jump, a low thud at the freeze, then the face's own weather sound.
// A screen that first saw the weather during the shuffle (reload) shows the final area at once.
// Reduced motion: only the last 3 decoys, frozen after ~1 s.
// Storm Surge highlights the squares the +1 sea reaches this round (no shuffle). Rain = blue, heat = orange.
const TINTS = {
  rain: { fill: "rgba(74,127,160,0.34)", line: "var(--sea)", soft: "rgba(74,127,160,0.10)", decoy: "rgba(74,127,160,0.42)", real: "rgba(74,127,160,0.52)" },
  heat: { fill: "rgba(232,128,52,0.36)", line: "#C8571E", soft: "rgba(232,128,52,0.10)", decoy: "rgba(232,128,52,0.42)", real: "rgba(232,128,52,0.54)" },
  surge: { fill: "rgba(31,74,85,0.38)", line: "var(--deep-water)", soft: "rgba(31,74,85,0.10)", decoy: "rgba(31,74,85,0.42)", real: "rgba(31,74,85,0.5)" },
};

const STEPS = [[-1, 0], [1, 0], [0, -1], [0, 1]];
const rc = (k) => k.split(",").map(Number);
const REDUCED_TIMES = [0, 300, 650];

// Fallback when the state has no `reach` (old saves): one step outside the shape, on the board.
function reachOf(area, rows, cols) {
  const set = new Set(area);
  const out = new Set();
  area.forEach((k) => {
    const [r, c] = rc(k);
    STEPS.forEach(([dr, dc]) => {
      const nr = r + dr;
      const nc = c + dc;
      const nk = `${nr},${nc}`;
      if (nr >= 0 && nc >= 0 && nr < rows && nc < cols && !set.has(nk)) out.add(nk);
    });
  });
  return [...out];
}

// The shuffle plan this screen plays: the server's decoys and times (reduced motion: the last 3).
function planOf(ws, shuffle) {
  const sh = ws?.shuffle;
  if (ws?.kind !== "shape" || !shuffle?.live || !sh?.decoys?.length || !shuffle.startMs) return null;
  if (shuffle.reduced) {
    const decoys = sh.decoys.slice(-REDUCED_TIMES.length);
    return { decoys, times: REDUCED_TIMES.slice(0, decoys.length), freeze: WEATHER_TIMING_MS.REDUCED_SHUFFLE, start: shuffle.startMs };
  }
  const times = (sh.times_ms || []).slice(0, sh.decoys.length);
  return { decoys: sh.decoys.slice(0, times.length), times, freeze: Number(sh.freeze_ms) || WEATHER_TIMING_MS.REVEAL, start: shuffle.startMs };
}

// 3D BOARD: every highlight sits on the square's top face (geom.vis: the part no taller neighbour
// covers), so it lifts with the block.
export default function WeatherArea({ ws, round, gridRows, gridCols, geom, shuffle = null }) {
  const tint = TINTS[ws?.tint] || TINTS.rain;
  const area = useMemo(() => (ws?.area ? [...ws.area] : []), [ws?.area]); // eslint-disable-line react-hooks/exhaustive-deps
  const key = ws ? `${round}-${ws.face}-${area.join("|")}` : null;
  const reach = useMemo(
    () => (ws?.reach?.length ? ws.reach.filter((k) => !area.includes(k)) : reachOf(area, gridRows, gridCols)),
    [key, gridRows, gridCols], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const plan = useMemo(() => planOf(ws, shuffle), [key, shuffle?.live, shuffle?.reduced, shuffle?.startMs]); // eslint-disable-line react-hooks/exhaustive-deps
  const [, bump] = useReducer((x) => x + 1, 0);
  const soundRef = useRef({ key: null, idx: -1, frozen: false });

  // Where the shuffle is now (server clock): the decoy index, or frozen.
  const t = plan ? serverNow() - plan.start : Infinity;
  const frozen = !plan || t >= plan.freeze;
  let idx = -1;
  if (plan && !frozen) {
    for (let i = 0; i < plan.times.length; i += 1) if (plan.times[i] <= t) idx = i;
  }

  // Re-render at the next jump / the freeze.
  useEffect(() => {
    if (!plan || frozen) return undefined;
    const next = plan.times.find((x) => x > t) ?? plan.freeze;
    const timer = setTimeout(bump, Math.max(0, next - t) + 4);
    return () => clearTimeout(timer);
  }); // eslint-disable-line react-hooks/exhaustive-deps

  // Sounds: a tick per jump, the thud at the freeze and then the face's sound (once per event).
  useEffect(() => {
    if (!plan) return;
    const s = soundRef.current;
    if (s.key !== key) soundRef.current = { key, idx: -1, frozen: false };
    const cur = soundRef.current;
    if (!frozen && idx > cur.idx) {
      cur.idx = idx;
      if (idx >= 0 && t - plan.times[idx] < 150) S.areaTick();
    }
    if (frozen && !cur.frozen) {
      cur.frozen = true;
      if (t - plan.freeze < 1500) {
        S.areaThud();
        const face = ws?.face;
        setTimeout(() => S.weatherResult(face), 260);
      }
    }
  });

  if (!ws || ws.kind === "none") return null;
  const box = (r, c) => {
    const v = geom.vis(`${r},${c}`);
    return { left: v.left, top: v.top, width: v.width, height: v.height };
  };

  if (ws.kind === "surge") {
    return (
      <div data-testid="weather-area" data-kind="surge" data-tint={ws.tint} data-shuffle="none" className="absolute inset-0 pointer-events-none" style={{ zIndex: LAYER.weather }}>
        {(ws.area || []).map((k) => {
          const [r, c] = rc(k);
          return (
            <span
              key={k}
              data-testid={`weather-surge-cell-${r},${c}`}
              className="absolute rounded-sm weather-surge-in"
              style={{
                ...box(r, c),
                background: tint.fill,
                boxShadow: `inset 0 0 0 2px ${tint.line}`,
                animationDelay: `${c * 90}ms`,
              }}
            />
          );
        })}
      </div>
    );
  }

  const decoy = !frozen && idx >= 0 ? plan.decoys[idx] : null;
  const pulse = !!plan && frozen; // this screen watched the shuffle: the real area pops + the reach fades in
  return (
    <div
      data-testid="weather-area"
      data-kind="shape"
      data-tint={ws.tint}
      data-stopped={String(frozen)}
      data-shuffle={plan ? (frozen ? "frozen" : "running") : "none"}
      data-decoy-index={decoy ? idx : undefined}
      data-size={area.length}
      className="absolute inset-0 pointer-events-none"
      style={{ zIndex: LAYER.weather }}
    >
      {decoy &&
        decoy.map((k) => {
          const [r, c] = rc(k);
          return (
            <span
              key={`decoy-${idx}-${k}`}
              data-testid={`weather-decoy-cell-${r},${c}`}
              className="absolute rounded-sm weather-decoy-in"
              style={{ ...box(r, c), background: tint.decoy, boxShadow: `inset 0 0 0 2.5px ${tint.line}` }}
            />
          );
        })}
      {frozen &&
        reach.map((k) => {
          const [r, c] = rc(k);
          return (
            <span
              key={`reach-${k}`}
              data-testid={`weather-reach-cell-${r},${c}`}
              className={`absolute rounded-sm ${pulse ? "weather-reach-in" : ""}`}
              style={{ ...box(r, c), background: tint.soft, outline: `1.5px dashed ${tint.line}`, outlineOffset: -2 }}
            />
          );
        })}
      {frozen &&
        area.map((k) => {
          const [r, c] = rc(k);
          return (
            <span
              key={`real-${k}`}
              data-testid={`weather-shape-cell-${r},${c}`}
              className={`absolute rounded-sm ${pulse ? "weather-freeze-pulse" : ""}`}
              style={{
                ...box(r, c),
                background: tint.real,
                boxShadow: `inset 0 0 0 3px ${tint.line}, 0 0 0 2px rgba(255,255,255,0.75)`,
              }}
            />
          );
        })}
    </div>
  );
}
